<template>
    <v-container fluid class="ai-cost pa-6">
        <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px">
            <div>
                <h1 class="text-h4 font-weight-bold">AI Cost and Usage</h1>
                <div class="text-subtitle-1 grey--text">
                    AI spend across DGTasks (OpenAI), PlanMyTour and GoGroupBooking (Claude)
                </div>
            </div>
            <v-spacer />
            <v-menu v-model="monthMenu" :close-on-content-click="false" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                    <v-text-field :value="monthLabel" label="Month (optional)" placeholder="All periods"
                        prepend-inner-icon="mdi-calendar-month" readonly clearable outlined dense hide-details
                        style="max-width: 220px" v-bind="attrs" v-on="on" @click:clear="clearMonth" />
                </template>
                <v-date-picker v-model="month" type="month" no-title :max="maxMonth"
                    @input="monthMenu = false" @change="load" />
            </v-menu>
            <v-btn outlined color="deep-purple" @click="switcherOpen = true">
                <v-icon left small>mdi-swap-horizontal</v-icon> AI Switcher
            </v-btn>
            <span v-if="loadedAt" class="caption grey--text">Updated {{ loadedAt }}</span>
            <v-btn outlined color="primary" :loading="loading" @click="load">
                <v-icon left small>mdi-refresh</v-icon> Refresh
            </v-btn>
        </div>

        <v-dialog v-model="switcherOpen" max-width="520">
            <v-card>
                <v-card-title class="text-h6">AI Switcher</v-card-title>
                <v-card-text>
                    <v-alert type="info" text dense>Coming soon. Switching is not active yet. DGTasks currently runs on OpenAI; PlanMyTour and GoGroupBooking run on Claude.</v-alert>
                    <div class="body-2 mb-3">Planned: choose which AI each app uses, or one for all apps.</div>
                    <v-select :items="['All apps', ...apps.map(a => a.name)]" value="All apps" label="Apply to" outlined dense disabled />
                    <v-select :items="['Claude', 'ChatGPT', 'Grok']" value="Claude (PlanMyTour, GoGroupBooking) / OpenAI (DGTasks)" label="Current provider" outlined dense disabled hide-details />
                </v-card-text>
                <v-card-actions><v-spacer /><v-btn text @click="switcherOpen = false">Close</v-btn></v-card-actions>
            </v-card>
        </v-dialog>

        <div v-if="loading && !apps.length" class="text-center py-12">
            <v-progress-circular indeterminate color="primary" size="48" />
            <div class="caption grey--text mt-3">Reading all three apps…</div>
        </div>

        <template v-else>
            <!-- Alerts -->
            <v-alert v-for="a in failedApps" :key="'err-' + a.app" type="error" dense text class="mb-2">
                <strong>{{ a.name }}</strong> could not be read: {{ a.error }}
            </v-alert>
            <v-alert v-for="a in spikeApps" :key="'spike-' + a.app" type="warning" dense prominent class="mb-2">
                <div class="font-weight-bold">Unusual AI spend in {{ a.name }}</div>
                <div v-for="(r, i) in a.data.spike.reasons" :key="i" class="body-2">{{ r }}</div>
            </v-alert>
            <v-alert v-for="a in blockedApps" :key="'blk-' + a.app" type="error" dense outlined class="mb-2">
                <strong>{{ a.name }}</strong> has reached its daily AI budget: AI features there are blocked until midnight
                ({{ a.data.blocked_today }} calls blocked today).
            </v-alert>

            <!-- ── Overall ───────────────────────────────────────────── -->
            <div class="section-title mt-2 mb-3">
                <v-icon left color="primary">mdi-earth</v-icon> Overall
                <span class="caption grey--text ml-2" v-if="okApps.length < apps.length">
                    (excluding {{ failedApps.map(a => a.name).join(', ') }})
                </span>
            </div>

            <v-row dense>
                <v-col v-for="t in overallTiles" :key="t.key" cols="6" sm="4" :md="month ? 4 : 2">
                    <v-card outlined class="pa-3 text-center" height="100%">
                        <div class="caption grey--text">{{ t.label }}</div>
                        <div class="text-h6 font-weight-bold">${{ usd(t.cost) }}</div>
                        <div class="caption grey--text">{{ num(t.calls) }} calls</div>
                        <div v-if="t.change !== null" class="caption"
                            :class="t.change > 0 ? 'red--text' : 'green--text'">
                            {{ t.change > 0 ? '▲' : '▼' }} {{ Math.abs(t.change) }}% vs {{ t.vs }}
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <v-row class="mt-1">
                <v-col cols="12" md="8">
                    <v-card height="100%">
                        <v-card-title class="text-subtitle-1 font-weight-bold">
                            Daily spend, {{ month ? monthLabel : 'last 30 days' }} (all apps)
                            <v-spacer />
                            <span v-for="a in okApps" :key="'lg-' + a.app" class="caption mr-3">
                                <span class="legend-dot" :style="{ background: appColor(a.app) }" /> {{ a.name }}
                            </span>
                        </v-card-title>
                        <v-card-text>
                            <bar-chart :bars="overallDailyBars" :height="200" compact />
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="4">
                    <v-card height="100%">
                        <v-card-title class="text-subtitle-1 font-weight-bold">Share by app</v-card-title>
                        <v-card-text>
                            <div v-if="month" class="caption grey--text mb-3">{{ monthLabel }}</div>
                            <v-btn-toggle v-else v-model="sharePeriod" mandatory dense class="mb-3">
                                <v-btn small value="today">Today</v-btn>
                                <v-btn small value="week">This week</v-btn>
                                <v-btn small value="month">Month</v-btn>
                                <v-btn small value="all_time">All time</v-btn>
                            </v-btn-toggle>
                            <div v-for="row in appShare" :key="'sh-' + row.app" class="mb-3">
                                <div class="d-flex justify-space-between body-2">
                                    <span><span class="legend-dot" :style="{ background: appColor(row.app) }" /> {{ row.name }}</span>
                                    <span class="font-weight-medium">${{ usd(row.cost) }} · {{ row.pct }}%</span>
                                </div>
                                <v-progress-linear :value="row.pct" :color="appColor(row.app)" height="8" rounded
                                    background-color="grey lighten-3" />
                            </div>
                            <v-divider class="my-3" />
                            <div v-if="!month" class="caption grey--text">
                                Month projection (all apps):
                                <strong class="black--text">{{ overallProjection === null ? '—' : '$' + usd(overallProjection) }}</strong>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-card class="mt-4">
                <v-card-title class="text-subtitle-1 font-weight-bold">
                    AI Feature Usage Breakdown (all apps)
                    <v-spacer />
                    <v-btn-toggle v-if="!month" v-model="featurePeriod" mandatory dense>
                        <v-btn small value="today">Today</v-btn>
                        <v-btn small value="week">Week</v-btn>
                        <v-btn small value="month">Month</v-btn>
                        <v-btn small value="all_time">All time</v-btn>
                    </v-btn-toggle>
                </v-card-title>
                <v-data-table :headers="overallFeatureHeaders" :items="overallFeatures" dense
                    :items-per-page="15" sort-by="cost" sort-desc>
                    <template v-slot:[`item.name`]="{ item }">
                        <v-chip x-small dark :color="appColor(item.app)">{{ item.name }}</v-chip>
                    </template>
                    <template v-slot:[`item.source`]="{ item }">{{ sourceLabel(item.source) }}</template>
                    <template v-slot:[`item.share`]="{ item }">
                        <div class="d-flex align-center" style="gap: 6px; min-width: 120px">
                            <v-progress-linear :value="item.share" :color="appColor(item.app)" height="6" rounded
                                background-color="grey lighten-3" />
                            <span class="caption">{{ item.share }}%</span>
                        </div>
                    </template>
                    <template v-slot:[`item.cost`]="{ item }"><strong>${{ usd(item.cost) }}</strong></template>
                </v-data-table>
            </v-card>

            <!-- ── Per app ───────────────────────────────────────────── -->
            <div class="section-title mt-8 mb-2">
                <v-icon left color="primary">mdi-apps</v-icon> Per app
            </div>
            <v-card>
                <v-tabs v-model="appTab" show-arrows grow height="auto" class="app-tabs">
                    <v-tab v-for="a in apps" :key="'tab-' + a.app" class="app-tab">
                        <div class="app-tab-inner">
                            <div class="d-flex align-center mb-2">
                                <span class="legend-dot big" :style="{ background: appColor(a.app) }" />
                                <span class="text-h6 font-weight-bold">{{ a.name }}</span>
                                <v-icon v-if="!a.ok" small color="error" class="ml-2">mdi-alert-circle</v-icon>
                                <v-icon v-else-if="a.data.spike.is_spike" small color="warning" class="ml-2">mdi-alert</v-icon>
                            </div>
                            <template v-if="a.ok">
                                <div class="d-flex" style="gap: 8px">
                                    <div v-for="t in appHeaderTiles(a)" :key="t.key" class="tab-stat">
                                        <div class="caption grey--text">{{ t.label }}</div>
                                        <div class="text-h6 font-weight-bold">${{ usd(t.cost) }}</div>
                                        <div class="caption grey--text">{{ num(t.calls) }} calls</div>
                                    </div>
                                </div>
                                <div class="d-flex justify-space-between body-2 mt-2">
                                    <span class="font-weight-medium">Daily budget</span>
                                    <span>
                                        ${{ usd(a.data.snapshot.spend_usd) }}
                                        <template v-if="a.data.snapshot.limit_usd > 0">
                                            / ${{ usd(a.data.snapshot.limit_usd) }} ({{ Math.round(a.data.snapshot.percentage) }}%)
                                        </template>
                                        <em v-else>/ no limit</em>
                                    </span>
                                </div>
                                <v-progress-linear v-if="a.data.snapshot.limit_usd > 0"
                                    :value="Math.min(a.data.snapshot.percentage, 100)" height="8" rounded
                                    :color="budgetColor(a.data.snapshot.percentage)" background-color="grey lighten-3" />
                                <div v-else class="caption orange--text text--darken-3">Uncapped: no daily limit</div>
                                <div class="d-flex align-center mt-2" style="gap: 8px" @click.stop @keydown.stop @keyup.stop>
                                    <v-text-field v-model.number="limitInputs[a.app]" type="number" min="0" prefix="$"
                                        label="Budget (USD), 0 = no limit" outlined dense hide-details />
                                    <v-btn color="primary" depressed :loading="savingApp === a.app"
                                        :disabled="!canSave(a)" @click.stop="saveLimit(a)">Save</v-btn>
                                </div>
                                <div class="d-flex align-start mt-2" style="gap: 8px" @click.stop @keydown.stop @keyup.stop>
                                    <v-combobox v-model="emailInputs[a.app]" multiple chips small-chips deletable-chips
                                        clearable outlined dense hide-selected append-icon="" :rules="[emailsValid]"
                                        label="Price change and spend alert emails"
                                        :hint="a.data.alert_emails && a.data.alert_emails.custom ? 'Type an email, press Enter' : 'Using the default address; type an email, press Enter'"
                                        persistent-hint />
                                    <v-btn color="primary" depressed :loading="savingEmailsApp === a.app"
                                        :disabled="!canSaveEmails(a)" @click.stop="saveEmails(a)">Save</v-btn>
                                </div>
                            </template>
                        </div>
                    </v-tab>
                </v-tabs>
                <v-divider />
                <v-tabs-items v-model="appTab">
                    <v-tab-item v-for="a in apps" :key="'app-' + a.app">
                        <div class="pa-4">
                            <v-alert v-if="!a.ok" type="error" text dense>{{ a.error }}</v-alert>

                            <template v-else>
                                <div class="caption grey--text">
                                    Budget set on: {{ a.data.snapshot.source === 'database' ? 'this page / app page' : 'server .env' }}
                                    · .env default: {{ a.data.snapshot.default_limit_usd ? '$' + usd(a.data.snapshot.default_limit_usd) : 'none' }}
                                    · Suggested ~${{ usd(suggestedLimit(a)) }}/day (3x the 14-day average, min $5)
                                </div>
                                <div class="caption grey--text mt-2">
                                    Yesterday ${{ usd(a.data.totals.yesterday.cost) }}
                                    · last month ${{ usd(a.data.totals.last_month.cost) }}
                                    · all time ${{ usd(a.data.totals.all_time.cost) }}
                                    · 14-day average ${{ usd(a.data.spike.avg_daily_cost) }}/day
                                    · busiest ${{ usd(a.data.spike.max_daily_cost) }}
                                    · avg ${{ usd(a.data.spike.avg_cost_per_call) }} per call
                                    (today ${{ usd(a.data.spike.today_cost_per_call) }})
                                    <span v-if="a.data.blocked_today"> · <span class="red--text">{{ a.data.blocked_today }} blocked today</span></span>
                                </div>

                                <v-row class="mt-2">
                    <!-- Daily chart -->
                    <v-col cols="12" md="7">
                        <v-card height="100%">
                            <v-card-title class="text-subtitle-1 font-weight-bold">Daily spend, {{ month ? monthLabel : 'last 30 days' }}</v-card-title>
                            <v-card-text>
                                <bar-chart :bars="appDailyBars(a)" :height="180" :color="appColor(a.app)" compact />
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- By model -->
                    <v-col cols="12" md="5">
                        <v-card height="100%">
                            <v-card-title class="text-subtitle-1 font-weight-bold">Spend by model, {{ month ? monthLabel : 'this month' }}</v-card-title>
                            <v-simple-table dense>
                                <tbody>
                                    <tr v-for="m in byModel(a)" :key="m.model">
                                        <td>{{ m.model || '—' }}</td>
                                        <td class="text-right">{{ num(m.calls) }} calls</td>
                                        <td class="text-right font-weight-medium">${{ usd(m.cost) }}</td>
                                    </tr>
                                    <tr v-if="!byModel(a).length">
                                        <td class="grey--text caption">No calls in this period.</td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </v-card>
                    </v-col>

                    <!-- Features -->
                    <v-col cols="12">
                        <v-card>
                            <v-card-title class="text-subtitle-1 font-weight-bold">AI Feature Usage Breakdown</v-card-title>
                            <v-data-table :headers="appFeatureHeadersView" :items="appFeatureItems(a)" dense
                                :items-per-page="10" :custom-sort="sortFeatures" sort-by="all_time" sort-desc>
                                <template v-slot:[`item.source`]="{ item }">{{ sourceLabel(item.source) }}</template>
                                <template v-for="p in ['today', 'week', 'month', 'all_time']" v-slot:[`item.${p}`]="{ item }">
                                    <div :key="p">
                                        <span :class="p === 'all_time' ? 'font-weight-bold' : ''">${{ usd(item[p].cost) }}</span>
                                        <div class="caption grey--text">{{ num(item[p].calls) }} calls</div>
                                    </div>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-col>
                                </v-row>
                            </template>
                        </div>
                    </v-tab-item>
                </v-tabs-items>
            </v-card>

            <div class="caption grey--text mt-6">
                Costs are metered from each response's token usage. PlanMyTour and GoGroupBooking (Claude) use rates checked daily
                against Anthropic's pricing page (changes are applied automatically and emailed); DGTasks (OpenAI) uses estimated
                rates set in its config. An hourly check emails
                when an app's daily spend or cost per call jumps to {{ spikeMultiplier }}x its 14-day average or above.
                Days are each app's server day.
            </div>
        </template>
    </v-container>
</template>

<script>
import BarChart from './BarChart';

const APP_COLORS = { dgtasks: '#3F51B5', pmt: '#00897B', ggb: '#F4511E' };

const SOURCE_LABELS = {
    time_tracking_analysis: 'Time Tracking Analysis',
    sales_assessment: 'Sales Assessment',
    ai_chat: 'AI Chat',
    ai_chat_attachment_vision: 'AI Chat: Attachment Reading',
    meeting_summary: 'Meeting Summary',
    cv_analysis: 'CV Analysis',
    growth_ai: 'Employee Growth AI',
    voice_task_command: 'Voice Task Commands',
    growth_admin_insights: 'Growth Admin Insights',
    voice_transcribe: 'Voice Transcription (Whisper)',
    chat_voice_note: 'Chat Voice Notes (Whisper)',
    app_chat: 'AI Assistant',
};

export default {
    name: 'AiCostCenterComponent',
    components: { BarChart },

    data() {
        return {
            apps: [],
            loading: false,
            loadedAt: null,
            limitInputs: {},
            emailInputs: {},
            savingEmailsApp: null,
            switcherOpen: false,
            savingApp: null,
            appTab: 0,
            month: null,
            monthMenu: false,
            sharePeriod: 'month',
            featurePeriod: 'month',
            overallFeatureHeaders: [
                { text: 'App', value: 'name' },
                { text: 'Feature', value: 'source' },
                { text: 'Calls', value: 'calls', align: 'end' },
                { text: 'Share of spend', value: 'share', sortable: false },
                { text: 'Cost', value: 'cost', align: 'end' },
            ],
            appFeatureHeaders: [
                { text: 'Feature', value: 'source' },
                { text: 'Today', value: 'today', align: 'end' },
                { text: 'This week', value: 'week', align: 'end' },
                { text: 'This month', value: 'month', align: 'end' },
                { text: 'All time', value: 'all_time', align: 'end' },
            ],
        };
    },

    computed: {
        monthLabel() {
            if (!this.month) return '';
            const [y, m] = this.month.split('-').map(Number);
            return new Date(y, m - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });
        },
        maxMonth() {
            return this.localToday().slice(0, 7);
        },
        appFeatureHeadersView() {
            return this.month
                ? [{ text: 'Feature', value: 'source' }, { text: this.monthLabel, value: 'month', align: 'end' }]
                : this.appFeatureHeaders;
        },
        okApps() {
            return this.apps.filter(a => a.ok);
        },
        failedApps() {
            return this.apps.filter(a => !a.ok);
        },
        spikeApps() {
            return this.okApps.filter(a => a.data.spike?.is_spike);
        },
        blockedApps() {
            return this.okApps.filter(a => a.data.snapshot.limit_usd > 0 && a.data.snapshot.percentage >= 100);
        },
        spikeMultiplier() {
            return this.okApps[0]?.data.spike?.multiplier || 2;
        },
        overallTotals() {
            const keys = ['today', 'yesterday', 'week', 'month', 'last_month', 'all_time'];
            const out = {};
            keys.forEach(k => {
                out[k] = this.okApps.reduce((s, a) => ({
                    cost: s.cost + (a.data.totals[k]?.cost || 0),
                    calls: s.calls + (a.data.totals[k]?.calls || 0),
                }), { cost: 0, calls: 0 });
            });
            return out;
        },
        overallTiles() {
            const t = this.overallTotals;
            if (this.month) {
                const sum = this.okApps.reduce((x, a) => ({
                    cost: x.cost + (a.data.month_view?.total.cost || 0),
                    calls: x.calls + (a.data.month_view?.total.calls || 0),
                }), { cost: 0, calls: 0 });
                return [{ key: 'month', label: this.monthLabel, ...sum, change: null }];
            }
            const change = (cur, prev) => (prev > 0 ? Math.round((cur - prev) / prev * 100) : null);
            return [
                { key: 'today', label: 'Today', ...t.today, change: change(t.today.cost, t.yesterday.cost), vs: 'yesterday' },
                { key: 'yesterday', label: 'Yesterday', ...t.yesterday, change: null },
                { key: 'week', label: 'This week', ...t.week, change: null },
                { key: 'month', label: 'This month', ...t.month, change: null },
                { key: 'last_month', label: 'Last month', ...t.last_month, change: null },
                { key: 'all_time', label: 'All time', ...t.all_time, change: null },
            ];
        },
        overallDailyBars() {
            const byDate = {};
            this.okApps.forEach(a => {
                this.dailyRows(a).forEach(d => {
                    byDate[d.date] = byDate[d.date] || { date: d.date, total: 0, calls: 0, parts: {} };
                    byDate[d.date].total += d.cost;
                    byDate[d.date].calls += d.calls;
                    byDate[d.date].parts[a.app] = d.cost;
                });
            });
            const today = this.localToday();
            return Object.values(byDate).sort((x, y) => x.date.localeCompare(y.date)).map(d => ({
                key: d.date,
                label: d.date.slice(8),
                value: d.total,
                display: '$' + d.total.toFixed(2),
                highlight: !this.month && d.date === today,
                segments: this.okApps.map(a => ({ value: d.parts[a.app] || 0, color: this.appColor(a.app) })),
                tooltip: `${d.date}: $${d.total.toFixed(2)} (${d.calls} calls)\n`
                    + this.okApps.map(a => `${a.name}: $${(d.parts[a.app] || 0).toFixed(2)}`).join('\n'),
            }));
        },
        appShare() {
            const k = this.sharePeriod;
            const costOf = a => (this.month ? a.data.month_view?.total.cost : a.data.totals[k]?.cost) || 0;
            const total = this.okApps.reduce((s, a) => s + costOf(a), 0);
            return this.okApps.map(a => {
                const cost = costOf(a);
                return { app: a.app, name: a.name, cost, pct: total > 0 ? Math.round(cost / total * 100) : 0 };
            });
        },
        overallProjection() {
            const now = new Date();
            const daysSoFar = now.getDate();
            const inMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            const month = this.overallTotals.month.cost;
            return month > 0 ? (month / daysSoFar) * inMonth : null;
        },
        overallFeatures() {
            const k = this.featurePeriod;
            const rows = [];
            this.okApps.forEach(a => {
                if (this.month) {
                    (a.data.month_view?.by_source || []).forEach(s => rows.push({ id: a.app + ':' + s.source, app: a.app, name: a.name, source: s.source, cost: s.cost, calls: s.calls }));
                    return;
                }
                a.data.by_source.forEach(s => {
                    if ((s[k]?.calls || 0) > 0) {
                        rows.push({ id: a.app + ':' + s.source, app: a.app, name: a.name, source: s.source, cost: s[k].cost, calls: s[k].calls });
                    }
                });
            });
            const total = rows.reduce((s, r) => s + r.cost, 0);
            return rows.map(r => ({ ...r, share: total > 0 ? Math.round(r.cost / total * 100) : 0 }));
        },
    },

    mounted() {
        this.load();
    },

    methods: {
        async load() {
            this.loading = true;
            const res = await this.$axios.get('api/ai-cost/overview', this.month ? { month: this.month } : {});
            if (res.data?.response) {
                this.apps = res.data.apps;
                this.apps.filter(a => a.ok).forEach(a => {
                    this.$set(this.limitInputs, a.app, a.data.snapshot.limit_usd);
                    this.$set(this.emailInputs, a.app, [...(a.data.alert_emails?.list || [])]);
                });
                this.loadedAt = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
            } else {
                this.$toast.error(res.status === 403 ? 'Only admins can view AI cost.' : (res.data?.message || 'Failed to load AI cost'));
            }
            this.loading = false;
        },

        clearMonth() {
            this.month = null;
            this.load();
        },

        dailyRows(a) {
            return (this.month ? a.data.month_view?.daily : a.data.daily) || [];
        },

        byModel(a) {
            return (this.month ? a.data.month_view?.by_model : a.data.by_model) || [];
        },

        appFeatureItems(a) {
            if (!this.month) return a.data.by_source;
            return (a.data.month_view?.by_source || []).map(s => ({ source: s.source, month: { cost: s.cost, calls: s.calls } }));
        },

        emailsValid(list) {
            const bad = (list || []).filter(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e).trim()));
            return !bad.length || 'Not a valid email: ' + bad.join(', ');
        },

        canSaveEmails(a) {
            const cur = (this.emailInputs[a.app] || []).map(e => String(e).trim().toLowerCase());
            const saved = (a.data.alert_emails?.list || []).map(e => e.toLowerCase());
            return this.emailsValid(cur) === true && cur.join(',') !== saved.join(',');
        },

        async saveEmails(a) {
            const emails = (this.emailInputs[a.app] || []).map(e => String(e).trim().toLowerCase()).filter(Boolean);
            this.savingEmailsApp = a.app;
            const res = await this.$axios.post('api/ai-cost/set-emails', { app: a.app, emails });
            if (res.data?.response) {
                const i = this.apps.findIndex(x => x.app === a.app);
                this.$set(this.apps, i, res.data.app);
                this.$set(this.emailInputs, a.app, [...(res.data.app.data.alert_emails?.list || [])]);
                this.$toast.success(res.data.message);
            } else {
                this.$toast.error(res.data?.message || 'Failed to update the alert emails');
            }
            this.savingEmailsApp = null;
        },

        canSave(a) {
            const v = this.limitInputs[a.app];
            return v !== '' && v !== null && v !== undefined && Number(v) >= 0
                && Number(v) !== Number(a.data.snapshot.limit_usd);
        },

        async saveLimit(a) {
            const value = Number(this.limitInputs[a.app]);
            if (value === 0 && !confirm(`Remove ${a.name}'s daily AI budget? Its AI spend will be uncapped.`)) {
                return;
            }
            this.savingApp = a.app;
            const res = await this.$axios.post('api/ai-cost/set-limit', { app: a.app, limit_usd: value });
            if (res.data?.response) {
                const i = this.apps.findIndex(x => x.app === a.app);
                this.$set(this.apps, i, res.data.app);
                this.limitInputs[a.app] = res.data.app.data.snapshot.limit_usd;
                this.$toast.success(res.data.message);
            } else {
                this.$toast.error(res.data?.message || 'Failed to update the budget');
            }
            this.savingApp = null;
        },

        suggestedLimit(a) {
            return Math.max(5, Math.ceil((a.data.spike.avg_daily_cost || 0) * 3));
        },

        appHeaderTiles(a) {
            const t = a.data.totals;
            if (this.month) {
                return [{ key: 'month', label: this.monthLabel, ...(a.data.month_view?.total || { cost: 0, calls: 0 }) }];
            }
            return [
                { key: 'today', label: 'Today', ...t.today },
                { key: 'week', label: 'This week', ...t.week },
                { key: 'month', label: 'This month', ...t.month },
            ];
        },

        appDailyBars(a) {
            const rows = this.dailyRows(a);
            const last = this.month ? -1 : rows.length - 1;
            return rows.map((d, i) => ({
                key: d.date,
                label: d.date.slice(8),
                value: d.cost,
                display: '$' + d.cost.toFixed(2),
                highlight: i === last,
                tooltip: `${d.date}: $${d.cost.toFixed(4)} (${d.calls} calls)`,
            }));
        },

        sortFeatures(items, sortBy, sortDesc) {
            const key = sortBy[0];
            if (!key) return items;
            const val = r => (key === 'source' ? this.sourceLabel(r.source) : (r[key]?.cost || 0));
            return [...items].sort((x, y) => {
                const a = val(x);
                const b = val(y);
                const c = typeof a === 'string' ? a.localeCompare(b) : a - b;
                return sortDesc[0] ? -c : c;
            });
        },

        budgetColor(p) {
            return p >= 100 ? 'error' : p >= 80 ? 'orange' : 'green';
        },

        appColor(app) {
            return APP_COLORS[app] || '#757575';
        },

        localToday() {
            const d = new Date();
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        },

        sourceLabel(source) {
            return SOURCE_LABELS[source]
                || String(source || 'unknown').replace(/^ai_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        },

        usd(v) {
            const n = Number(v) || 0;
            return n >= 1 ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : n.toFixed(4);
        },

        num(v) {
            return (Number(v) || 0).toLocaleString();
        },
    },
};
</script>

<style scoped>
.section-title {
    font-size: 1.35rem;
    font-weight: 700;
    display: flex;
    align-items: center;
}
.legend-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 4px;
    vertical-align: middle;
}
.legend-dot.big {
    width: 14px;
    height: 14px;
    margin-right: 10px;
}
.app-tabs ::v-deep .v-tab {
    height: auto;
    min-width: 340px;
    padding: 14px 16px;
    text-transform: none;
    letter-spacing: normal;
    align-items: stretch;
    justify-content: flex-start;
    text-align: left;
}
.app-tab-inner {
    width: 100%;
    text-align: left;
}
.tab-stat {
    flex: 1;
    background: #f5f5f5;
    border-radius: 6px;
    padding: 6px 10px;
}
.tile {
    background: #f5f5f5;
    border-radius: 6px;
    padding: 10px 12px;
}
</style>
